import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { motion } from 'framer-motion';
import axios from 'axios';
import { toast } from 'sonner';
import { FaArrowLeft, FaAppleAlt } from 'react-icons/fa';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const DietCalculator = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    age: '',
    weight: '',
    height: '',
    gender: '',
    activity_level: '',
    goal: '',
    excluded_foods: ''
  });
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      const response = await axios.post(`${API}/diet/calculate`, {
        ...formData,
        age: parseInt(formData.age),
        weight: parseFloat(formData.weight),
        height: parseFloat(formData.height),
        excluded_foods: formData.excluded_foods ? formData.excluded_foods.split(',').map(f => f.trim()) : []
      }, { withCredentials: false });
      
      setResult(response.data);
      toast.success('¡Plan nutricional generado!');
    } catch (error) {
      toast.error('Error al calcular la dieta');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-green-50 via-emerald-50 to-teal-50 py-12 px-6">
      <div className="max-w-5xl mx-auto">
        <Button variant="outline" onClick={() => navigate('/dashboard')} className="mb-6" data-testid="back-button">
          <FaArrowLeft className="mr-2" /> Volver
        </Button>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-8"
        >
          <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-gradient-to-r from-green-400 to-emerald-600 text-white mb-4">
            <FaAppleAlt className="text-4xl" />
          </div>
          <h1 className="text-4xl lg:text-5xl font-bold mb-4" data-testid="calculator-title">
            Calculadora de Plan Nutricional
          </h1>
          <p className="text-lg text-gray-600">
            Genera tu plan de alimentación personalizado
          </p>
        </motion.div>

        <div className="grid lg:grid-cols-2 gap-8">
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.2 }}
          >
            <Card>
              <CardHeader>
                <CardTitle>Datos Personales</CardTitle>
                <CardDescription>Completa la información para calcular tus macros</CardDescription>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="age">Edad</Label>
                      <Input
                        id="age"
                        type="number"
                        value={formData.age}
                        onChange={(e) => setFormData({...formData, age: e.target.value})}
                        required
                        data-testid="calc-age-input"
                      />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="gender">Género</Label>
                      <Select value={formData.gender} onValueChange={(val) => setFormData({...formData, gender: val})} required>
                        <SelectTrigger data-testid="calc-gender-select">
                          <SelectValue placeholder="Selecciona" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="female">Femenino</SelectItem>
                          <SelectItem value="male">Masculino</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="weight">Peso (kg)</Label>
                      <Input
                        id="weight"
                        type="number"
                        step="0.1"
                        value={formData.weight}
                        onChange={(e) => setFormData({...formData, weight: e.target.value})}
                        required
                        data-testid="calc-weight-input"
                      />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="height">Altura (cm)</Label>
                      <Input
                        id="height"
                        type="number"
                        value={formData.height}
                        onChange={(e) => setFormData({...formData, height: e.target.value})}
                        required
                        data-testid="calc-height-input"
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="activity_level">Nivel de Actividad</Label>
                    <Select value={formData.activity_level} onValueChange={(val) => setFormData({...formData, activity_level: val})} required>
                      <SelectTrigger data-testid="calc-activity-select">
                        <SelectValue placeholder="Selecciona" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="sedentary">Sedentario</SelectItem>
                        <SelectItem value="light">Ligero (1-3 días/semana)</SelectItem>
                        <SelectItem value="moderate">Moderado (3-5 días/semana)</SelectItem>
                        <SelectItem value="active">Activo (6-7 días/semana)</SelectItem>
                        <SelectItem value="very_active">Muy Activo (2 veces/día)</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="goal">Objetivo</Label>
                    <Select value={formData.goal} onValueChange={(val) => setFormData({...formData, goal: val})} required>
                      <SelectTrigger data-testid="calc-goal-select">
                        <SelectValue placeholder="Selecciona" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="lose_weight">Perder Peso</SelectItem>
                        <SelectItem value="gain_muscle">Ganar Músculo</SelectItem>
                        <SelectItem value="maintain">Mantener</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="excluded_foods">Alimentos a Excluir (opcional)</Label>
                    <Textarea
                      id="excluded_foods"
                      placeholder="Ej: lácteos, gluten, mariscos (separados por coma)"
                      value={formData.excluded_foods}
                      onChange={(e) => setFormData({...formData, excluded_foods: e.target.value})}
                      data-testid="excluded-foods-input"
                    />
                  </div>

                  <Button type="submit" className="w-full" disabled={loading} data-testid="calculate-button">
                    {loading ? 'Generando tu plan...' : 'Calcular y Generar Plan'}
                  </Button>
                </form>
              </CardContent>
            </Card>
          </motion.div>

          {result && (
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              animate={{ opacity: 1, x: 0 }}
              className="space-y-6"
            >
              <Card className="bg-gradient-to-r from-green-500 to-emerald-600 text-white">
                <CardHeader>
                  <CardTitle>Tu Plan Nutricional</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 gap-4" data-testid="macros-result">
                    <div className="text-center p-4 bg-white/20 rounded-lg">
                      <div className="text-3xl font-bold">{result.calories}</div>
                      <div className="text-sm opacity-90">Calorías</div>
                    </div>
                    <div className="text-center p-4 bg-white/20 rounded-lg">
                      <div className="text-3xl font-bold">{result.protein}g</div>
                      <div className="text-sm opacity-90">Proteína</div>
                    </div>
                    <div className="text-center p-4 bg-white/20 rounded-lg">
                      <div className="text-3xl font-bold">{result.carbs}g</div>
                      <div className="text-sm opacity-90">Carbohidratos</div>
                    </div>
                    <div className="text-center p-4 bg-white/20 rounded-lg">
                      <div className="text-3xl font-bold">{result.fats}g</div>
                      <div className="text-sm opacity-90">Grasas</div>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {result.meals && result.meals.length > 0 && (
                <Card>
                  <CardHeader>
                    <CardTitle>Tu Plan de Comidas Personalizado</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    {result.meals.map((meal, idx) => (
                      <div key={idx} className="border-l-4 border-green-500 pl-4">
                        <div className="font-semibold">{meal.name} - {meal.time}</div>
                        <div className="text-sm text-gray-600">Calorías: {meal.calories}</div>
                        <ul className="text-sm mt-1">
                          {meal.foods?.map((food, i) => (
                            <li key={i}>• {food}</li>
                          ))}
                        </ul>
                      </div>
                    ))}
                  </CardContent>
                </Card>
              )}

              {result.recommendations && (
                <Card>
                  <CardHeader>
                    <CardTitle>Recomendaciones para Ti</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-sm text-gray-600 whitespace-pre-wrap">{result.recommendations}</p>
                  </CardContent>
                </Card>
              )}

              <Button onClick={() => navigate('/my-diet')} className="w-full" data-testid="view-diet-button">
                Ver Mi Plan Completo
              </Button>
            </motion.div>
          )}
        </div>
      </div>
    </div>
  );
};

export default DietCalculator;